import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiArrowLeft,
  FiCalendar,
  FiDollarSign,
  FiFileText,
  FiSend,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiRepeat
} from "react-icons/fi";
import * as estimationService from "../../services/estimationService";
import { getRole } from "../../components/Apicall";
import { PillButton, StatTile, StatusBadge, STATUS_TINT, TintButton } from "./EstimationButtons";
import { formatDateHyphenated } from "../../utils/dateFormat";

const STATUS_ICON = {
  DRAFT: FiFileText,
  SENT: FiSend,
  ACCEPTED: FiCheckCircle,
  DECLINED: FiXCircle,
  EXPIRED: FiClock,
  CONVERTED: FiRepeat
};

const thStyle = {
  background: "#F9FAFB",
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  padding: "12px 16px",
  borderBottom: "1px solid #E5E7EB",
  textAlign: "left"
};

const tdStyle = {
  padding: "12px 16px",
  fontSize: 14,
  color: "#374151",
  borderBottom: "1px solid #F3F4F6"
};

const cardStyle = {
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  marginBottom: 24,
  overflow: "hidden"
};

const cardHeaderStyle = {
  padding: "20px 24px",
  borderBottom: "1px solid #F3F4F6",
  fontSize: 16,
  fontWeight: 700,
  color: "#111827"
};

const cardBodyStyle = { padding: 24 };

function EstimationView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isOwner = getRole() === "3";

  const [loading, setLoading] = useState(true);
  const [estimation, setEstimation] = useState(null);
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [res, verRes] = await Promise.all([
        estimationService.getEstimation(id),
        estimationService.getVersions(id)
      ]);
      if (!res.success) {
        toast.error(res.message || "Quotation not found");
        navigate("/estimations");
        return;
      }
      setEstimation(res.data);
      setVersions(verRes?.data || []);
    } catch {
      toast.error("Failed to load quotation");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function runAction(action, successMsg) {
    setBusy(true);
    try {
      const res = await action();
      if (!res.success) throw new Error(res.message || "Action failed");
      toast.success(successMsg);
      await load();
    } catch (err) {
      toast.error(err.message || "Action failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleExportPdf() {
    try {
      await estimationService.exportEstimationPdf(id);
    } catch (err) {
      toast.error(err.message || "Failed to export PDF");
    }
  }

  if (loading || !estimation) {
    return (
      <div style={{ padding: 24, background: "#F3F4F6", minHeight: "100vh" }}>
        <div style={{ textAlign: "center", padding: 48, color: "#6B7280" }}>Loading…</div>
      </div>
    );
  }

  const currency = estimation.currency;
  const discountAmount =
    estimation.discount_type === "PERCENT"
      ? (Number(estimation.subtotal) * Number(estimation.discount_value)) / 100
      : Number(estimation.discount_value);
  const StatusIcon = STATUS_ICON[estimation.status];

  return (
    <div style={{ padding: 24, background: "#F3F4F6", minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>
      <button
        onClick={() => navigate("/estimations")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          padding: 0,
          color: "#6B7280",
          fontWeight: 600,
          fontSize: 13,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 20,
          cursor: "pointer"
        }}
      >
        <FiArrowLeft size={16} /> Back
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>
          Quotation {estimation.display_estimation_id}
          {estimation.version > 1 ? ` (v${estimation.version})` : ""}
        </h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {estimation.status === "DRAFT" && (
            <>
              <TintButton tint="blue" onClick={() => navigate(`/estimations/${id}/edit`)}>
                Edit
              </TintButton>
              <PillButton
                disabled={busy}
                onClick={() => runAction(() => estimationService.sendEstimation(id), "Quotation sent")}
              >
                Send to Customer
              </PillButton>
            </>
          )}
          {estimation.status === "SENT" && isOwner && (
            <>
              <PillButton
                disabled={busy}
                onClick={() => runAction(() => estimationService.acceptEstimation(id), "Marked as accepted")}
              >
                Mark Accepted
              </PillButton>
              <TintButton
                tint="red"
                disabled={busy}
                onClick={() => runAction(() => estimationService.declineEstimation(id), "Marked as declined")}
              >
                Mark Declined
              </TintButton>
              <TintButton tint="blue" onClick={() => navigate(`/estimations/${id}/edit`)}>
                Revise
              </TintButton>
            </>
          )}
          {estimation.status === "EXPIRED" && isOwner && (
            <TintButton tint="blue" onClick={() => navigate(`/estimations/${id}/edit`)}>
              Revise
            </TintButton>
          )}
          {estimation.status === "ACCEPTED" && isOwner && (
            <PillButton onClick={() => navigate(`/vendor_bookings?from_estimation=${id}`)}>
              Convert to Booking
            </PillButton>
          )}
          {estimation.status === "CONVERTED" && estimation.converted_booking_id && (
            <TintButton tint="purple" onClick={() => navigate(`/vendor_bookings/${estimation.converted_booking_id}`)}>
              View Booking
            </TintButton>
          )}
          <TintButton tint="gray" onClick={handleExportPdf}>
            Export PDF
          </TintButton>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
        <StatTile label="Valid Until" value={formatDateHyphenated(estimation.valid_until)} icon={FiCalendar} tint="blue" />
        <StatTile label="Status" value={<StatusBadge status={estimation.status} />} icon={StatusIcon} tint={STATUS_TINT[estimation.status]} />
        <StatTile label="Grand Total" value={`${currency} ${Number(estimation.grand_total).toFixed(2)}`} icon={FiDollarSign} tint="green" />
      </div>

      <div style={cardStyle}>
        <div style={cardHeaderStyle}>Customer</div>
        <div style={cardBodyStyle}>
          <div style={{ fontWeight: 600, color: "#111827" }}>{estimation.customer_name}</div>
          <div style={{ color: "#6B7280", fontSize: 14 }}>
            {estimation.phone_number} {estimation.email ? `· ${estimation.email}` : ""}
          </div>
          <div style={{ color: "#6B7280", fontSize: 14 }}>
            {[estimation.house_no, estimation.area, estimation.location, estimation.state].filter(Boolean).join(", ")}
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={cardHeaderStyle}>Line Items</div>
        <div style={cardBodyStyle}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Qty</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Unit Price</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(estimation.offerings || []).map((o) => (
                  <tr key={`o_${o.id}`}>
                    <td style={tdStyle}>
                      {[o.service_name, o.service_type_name, o.offering_name].filter(Boolean).join(" - ") || "Service"}
                    </td>
                    <td style={tdStyle}>{Number(o.quantity)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{currency} {Number(o.unit_price).toFixed(2)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{currency} {Number(o.line_total).toFixed(2)}</td>
                  </tr>
                ))}
                {(estimation.items || []).map((i) => (
                  <tr key={`i_${i.id}`}>
                    <td style={tdStyle}>{i.item_name}</td>
                    <td style={tdStyle}>{Number(i.quantity)} {i.unit_of_measure}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{currency} {Number(i.unit_price).toFixed(2)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{currency} {Number(i.line_total).toFixed(2)}</td>
                  </tr>
                ))}
                {(estimation.offerings || []).length === 0 && (estimation.items || []).length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ ...tdStyle, textAlign: "center", color: "#6B7280" }}>
                      No line items
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ borderTop: "1px solid #F3F4F6", marginTop: 8, paddingTop: 12, maxWidth: 320, marginLeft: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#374151" }}>
              <span>Subtotal</span>
              <span>{currency} {Number(estimation.subtotal).toFixed(2)}</span>
            </div>
            {Number(estimation.discount_value) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#C0335A" }}>
                <span>Discount</span>
                <span>-{currency} {discountAmount.toFixed(2)}</span>
              </div>
            )}
            {Number(estimation.tax_amount) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#374151" }}>
                <span>Tax</span>
                <span>{currency} {Number(estimation.tax_amount).toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, color: "#111827", borderTop: "1px solid #F3F4F6", marginTop: 4, paddingTop: 4 }}>
              <span>Grand Total</span>
              <span>{currency} {Number(estimation.grand_total).toFixed(2)}</span>
            </div>
          </div>

          {estimation.notes && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>Notes</div>
              <div style={{ color: "#6B7280", fontSize: 14 }}>{estimation.notes}</div>
            </div>
          )}
        </div>
      </div>

      {versions.length > 1 && (
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>Version History</div>
          <div style={cardBodyStyle}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {versions.map((v) => (
                <TintButton
                  key={v.id}
                  tint={v.is_latest ? "blue" : "gray"}
                  onClick={() => navigate(`/estimations/${v.id}`)}
                >
                  v{v.version} {v.is_latest ? "(latest)" : ""}
                </TintButton>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EstimationView;
